import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../supabaseClient';
import { trackPage } from '../utils/analytics';

function calculateAge(dob) {
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function isAutoGenerated(name) {
  if (!name) return true;
  // Auto-generated names look like "user_12345678" or are just email prefixes
  return /^user_[a-f0-9]{8}$/i.test(name);
}

export default function EmailConfirmed() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [needsSetup, setNeedsSetup] = useState(false);
  const [form, setForm]             = useState({ displayName: '', dob: '' });
  const [errors, setErrors]         = useState({});
  const [saving, setSaving]         = useState(false);

  useEffect(() => {
    trackPage('/confirmed');
  }, []);

  useEffect(() => {
    if (!profile) return;

    const needsName = isAutoGenerated(profile.display_name);
    const needsDob  = !profile.date_of_birth;

    if (needsName || needsDob) {
      setNeedsSetup(true);
      setForm(f => ({ ...f, displayName: needsName ? '' : profile.display_name }));
    } else if (profile.profile_photo_url) {
      // Already has a photo — go straight to feed
      navigate('/feed');
    } else {
      // No photo yet — go to onboarding
      const t = setTimeout(() => navigate('/onboarding'), 2500);
      return () => clearTimeout(t);
    }
  }, [profile, navigate]);

  function validate() {
    const e = {};
    if (!form.displayName.trim())          e.displayName = 'You need a display name.';
    if (form.displayName.length > 50)      e.displayName = 'Max 50 characters.';
    if (!form.dob)                         e.dob         = 'Date of birth is required.';
    else if (calculateAge(form.dob) < 18)  e.dob         = 'You must be 18 or older to join.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name:   form.displayName.trim(),
          date_of_birth:  form.dob,
        })
        .eq('id', user.id);

      if (error) throw error;
      await refreshProfile();
      navigate('/onboarding');
    } catch (err) {
      setErrors({ submit: err.message || 'Something went wrong. Try again.' });
    } finally {
      setSaving(false);
    }
  }

  // Still loading profile
  if (!profile) {
    return (
      <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p className="text-muted" style={{ fontFamily: 'var(--font-heading)', fontSize: '1.4rem' }}>
          Loading...
        </p>
      </div>
    );
  }

  // Google user needs to set display name and DOB
  if (needsSetup) {
    return (
      <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="container">
          <div className="card">
            <h2 style={{ fontFamily: 'var(--font-heading)', color: 'var(--gold)', fontSize: '2rem', marginBottom: 6 }}>
              One more thing
            </h2>
            <p className="text-muted mb-24" style={{ fontSize: '0.9rem' }}>
              Set your display name and date of birth before you continue.
            </p>

            <form onSubmit={handleSave} noValidate>
              <div className="form-group">
                <label htmlFor="displayName">Display name</label>
                <input
                  id="displayName"
                  type="text"
                  placeholder="How the internet will know you"
                  value={form.displayName}
                  onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                  maxLength={50}
                  autoFocus
                />
                {errors.displayName && <p className="form-error">{errors.displayName}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="dob">Date of birth</label>
                <input
                  id="dob"
                  type="date"
                  value={form.dob}
                  onChange={e => setForm(f => ({ ...f, dob: e.target.value }))}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                    .toISOString().split('T')[0]}
                  style={{ colorScheme: 'dark' }}
                />
                {errors.dob && <p className="form-error">{errors.dob}</p>}
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  You must be 18 or older. Your age personalizes your feed.
                </p>
              </div>

              {errors.submit && <p className="form-error mb-16">{errors.submit}</p>}

              <button className="btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save and continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Standard email confirmation landing — new user
  return (
    <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div className="container">
        <div className="card text-center">
          <h2 style={{ color: 'var(--valid-color)', fontFamily: 'var(--font-heading)', fontSize: '2rem' }}>
            Existence confirmed.
          </h2>
          <p className="text-muted mt-16" style={{ lineHeight: 1.8, fontSize: '0.95rem' }}>
            {profile?.profile_photo_url
              ? 'Welcome back. Taking you to the feed.'
              : 'You are, in fact, a real person. Now upload your photo and let the internet decide.'
            }
          </p>
          <Link to={profile?.profile_photo_url ? '/feed' : '/onboarding'}>
            <button className="btn-primary mt-24">
              {profile?.profile_photo_url ? 'Go to feed' : 'Upload your photo'}
            </button>
          </Link>
          <p className="text-muted mt-16" style={{ fontSize: '0.75rem' }}>
            Redirecting automatically...
          </p>
        </div>
      </div>
    </div>
  );
}
